/**
 * @fileoverview Playground command for executing prompts
 * @module @mutagent/cli/commands/playground
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { getSDKClient } from '../lib/sdk-client.js';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { MutagentError, handleError } from '../lib/errors.js';
import { playgroundLink, promptLink } from '../lib/ui-links.js';
import type { PromptStreamEvent } from '../types/index.js';

// =============================================================================
// Types
// =============================================================================

/**
 * Message object for prompt messages array
 */
interface PromptMessage {
  /** Role of the message sender */
  role: 'system' | 'user' | 'assistant';
  /** Content of the message */
  content: string;
}

/**
 * Options for the run command
 */
interface RunOptions {
  /** Input variables as JSON string */
  input?: string;
  /** Enable streaming mode */
  stream?: boolean;
  /** Model override */
  model?: string;
  /** System prompt text */
  system?: string;
  /** Human/user message text */
  human?: string;
  /** Full messages array as JSON string */
  messages?: string;
}

// =============================================================================
// SSE Parsing Utilities
// =============================================================================

/**
 * Parse an SSE message line
 */
function parseSSELine(line: string): { field: string; value: string } | null {
  if (!line || line.startsWith(':')) {
    return null; // Comment or empty line
  }

  const colonIndex = line.indexOf(':');
  if (colonIndex === -1) {
    return { field: line, value: '' };
  }

  const field = line.slice(0, colonIndex);
  let value = line.slice(colonIndex + 1);
  if (value.startsWith(' ')) {
    value = value.slice(1);
  }

  return { field, value };
}

/**
 * Parse SSE event data into a PromptStreamEvent
 */
function parsePromptStreamEvent(data: string): PromptStreamEvent | null {
  try {
    return JSON.parse(data) as PromptStreamEvent;
  } catch {
    return null;
  }
}

// =============================================================================
// Command Implementation
// =============================================================================

export function createPlaygroundCommand(): Command {
  const playground = new Command('playground')
    .description('Execute and test prompts interactively')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent playground run <prompt-id> --input '{"name": "John"}'
  ${chalk.dim('$')} mutagent playground run <prompt-id> --input '{}' --stream
  ${chalk.dim('$')} mutagent playground run <prompt-id> -i '{}' --model gpt-4-turbo
  ${chalk.dim('$')} mutagent playground run <prompt-id> --system "You are helpful" --human "Hello"
  ${chalk.dim('$')} mutagent playground run <prompt-id> --messages '[{"role":"user","content":"Hi"}]'

Input Format:
  The input must be a valid JSON object matching the prompt's input schema.
  Use --input for inline JSON.

Prompt Style Flags:
  Use --system and --human to quickly construct a messages array.
  Use --messages to provide a full messages array as JSON.

Streaming:
  Use --stream to receive tokens as they are generated.
  In JSON mode (--json), each token is output as a separate JSON object.
    `);

  // Run prompt
  playground
    .command('run')
    .description('Execute a prompt with input variables')
    .argument('<prompt-id>', 'Prompt ID to execute (from: mutagent prompts list)')
    .option('-i, --input <json>', 'Input variables as JSON')
    .option('-s, --stream', 'Stream the response')
    .option('-m, --model <model>', 'Override model')
    .option('--system <text>', 'Set system prompt text')
    .option('--human <text>', 'Set human/user message text')
    .option('--messages <json>', 'Pass full messages array as JSON string')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent playground run <prompt-id> --input '{"name": "John"}'
  ${chalk.dim('$')} mutagent playground run <prompt-id> --input '{}' --stream
  ${chalk.dim('$')} mutagent playground run <prompt-id> --system "You are helpful" --human "Hello"
  ${chalk.dim('$')} mutagent playground run <prompt-id> --input '{}' --model gpt-4-turbo --json

Input Methods (pick one, priority order):
  --system/--human            Quick system + user message ${chalk.green('(recommended)')}
  --input '{"key":"value"}'   Inline JSON variables
  --messages '[...]'          Full messages array
${chalk.dim('Hint: Test before evaluating: mutagent playground run <id> --input \'{"key":"value"}\'')}
    `)
    .action(async (promptId: string, options: RunOptions) => {
      const isJson = getJsonFlag(playground);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // Parse input
        const inputData = parseInput(options);

        const client = await getSDKClient();

        if (options.stream) {
          // Streaming mode
          await executeStreaming(client, promptId, inputData, options.model, isJson, output);
        } else {
          // Non-streaming mode
          const result = await client.executePrompt(promptId, inputData, {
            model: options.model,
          });

          if (isJson) {
            output.output({
              ...result,
              _links: {
                playground: playgroundLink(promptId),
                prompt: promptLink(promptId),
              },
            });
          } else {
            // Display result in human-readable format
            console.log(chalk.bold('\nExecution Result:'));
            console.log(chalk.gray('─'.repeat(50)));
            console.log(chalk.cyan('Output:'));
            console.log(result.output);
            console.log(chalk.gray('─'.repeat(50)));
            console.log(chalk.dim(`Model: ${result.model}`));
            console.log(chalk.dim(`Execution Time: ${String(result.executionTimeMs)}ms`));
            if (result.tokens) {
              console.log(chalk.dim(`Tokens: ${String(result.tokens.prompt)} prompt + ${String(result.tokens.completion)} completion = ${String(result.tokens.total)} total`));
            }
            if (result.cost !== undefined) {
              console.log(chalk.dim(`Cost: $${result.cost.toFixed(6)}`));
            }
            console.log(chalk.dim(`Playground: ${playgroundLink(promptId)}`));
            console.log();
          }
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  return playground;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Parse input from options (either --input or prompt style flags)
 */
function parseInput(options: RunOptions): Record<string, unknown> {
  // Check for conflicting options
  const hasPromptStyleFlags = options.system ?? options.human ?? options.messages;

  if (options.input && hasPromptStyleFlags) {
    throw new MutagentError(
      'INVALID_ARGUMENTS',
      'Cannot use --input with prompt style flags (--system, --human, --messages)',
      'Use either --input OR prompt style flags, not both'
    );
  }

  // Handle prompt style flags
  if (hasPromptStyleFlags) {
    return parsePromptStyleInput(options);
  }

  // Get input JSON from file or inline option
  const inputJson = getInputJson(options);

  return parseJsonInput(inputJson);
}

/**
 * Parse prompt style input (--system, --human, --messages)
 */
function parsePromptStyleInput(options: RunOptions): Record<string, unknown> {
  // If --messages is provided, parse and use directly
  if (options.messages) {
    if (options.system ?? options.human) {
      throw new MutagentError(
        'INVALID_ARGUMENTS',
        'Cannot use --messages with --system or --human',
        'Use either --messages OR --system/--human flags, not both'
      );
    }

    try {
      const parsed = JSON.parse(options.messages) as unknown;
      if (!Array.isArray(parsed)) {
        throw new MutagentError(
          'INVALID_INPUT',
          '--messages must be a JSON array',
          'Provide messages as [{"role": "user", "content": "Hello"}]'
        );
      }

      // Validate each message has role and content
      for (const msg of parsed) {
        if (typeof msg !== 'object' || msg === null) {
          throw new MutagentError(
            'INVALID_INPUT',
            'Each message must be an object',
            'Format: {"role": "system|user|assistant", "content": "..."}'
          );
        }
        const msgObj = msg as Record<string, unknown>;
        if (!msgObj.role || !msgObj.content) {
          throw new MutagentError(
            'INVALID_INPUT',
            'Each message must have role and content fields',
            'Format: {"role": "system|user|assistant", "content": "..."}'
          );
        }
      }

      return { messages: parsed as PromptMessage[] };
    } catch (err) {
      if (err instanceof MutagentError) {
        throw err;
      }
      throw new MutagentError(
        'INVALID_JSON',
        'Failed to parse --messages as JSON',
        err instanceof Error ? err.message : 'Ensure the input is valid JSON array'
      );
    }
  }

  // Construct messages array from --system and/or --human
  if (!options.system && !options.human) {
    throw new MutagentError(
      'MISSING_ARGUMENTS',
      'At least one of --system or --human is required when using prompt style flags',
      'Use --system "prompt" and/or --human "message"'
    );
  }

  const messages: PromptMessage[] = [];

  if (options.system) {
    messages.push({ role: 'system', content: options.system });
  }

  if (options.human) {
    messages.push({ role: 'user', content: options.human });
  }

  return { messages };
}

/**
 * Get input JSON string from file or inline option
 */
function getInputJson(options: RunOptions): string {
  if (options.input) {
    return options.input;
  }

  throw new MutagentError(
    'MISSING_ARGUMENTS',
    'Input is required',
    'Use --input \'{"key": "value"}\' or --system/--human/--messages flags'
  );
}

/**
 * Parse JSON string into a record object
 */
function parseJsonInput(inputJson: string): Record<string, unknown> {

  try {
    const parsed = JSON.parse(inputJson) as unknown;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new MutagentError(
        'INVALID_INPUT',
        'Input must be a JSON object',
        'Provide input as {"key": "value"} format'
      );
    }
    return parsed as Record<string, unknown>;
  } catch (err) {
    if (err instanceof MutagentError) {
      throw err;
    }
    throw new MutagentError(
      'INVALID_JSON',
      'Failed to parse input as JSON',
      err instanceof Error ? err.message : 'Ensure the input is valid JSON'
    );
  }
}

/**
 * Execute prompt with streaming
 */
async function executeStreaming(
  client: Awaited<ReturnType<typeof getSDKClient>>,
  promptId: string,
  input: Record<string, unknown>,
  model: string | undefined,
  isJson: boolean,
  output: OutputFormatter
): Promise<void> {
  const response = await client.executePromptStream(promptId, input, { model });

  if (!response.body) {
    throw new MutagentError(
      'STREAM_ERROR',
      'No response body received',
      'The server did not return a streaming response'
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  if (!isJson) {
    console.log(chalk.bold('\nStreaming Output:'));
    console.log(chalk.gray('─'.repeat(50)));
  }

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';

      for (const line of lines) {
        const parsed = parseSSELine(line);
        if (!parsed) continue;

        if (parsed.field === 'data') {
          const event = parsePromptStreamEvent(parsed.value);
          if (!event) continue;

          switch (event.type) {
            case 'prompt.start':
              if (isJson) {
                console.log(JSON.stringify({ type: 'start', timestamp: event.timestamp }));
              }
              break;

            case 'prompt.token':
              if (event.token) {
                if (isJson) {
                  console.log(JSON.stringify({ type: 'token', token: event.token }));
                } else {
                  process.stdout.write(event.token);
                }
              }
              break;

            case 'prompt.complete':
              if (isJson) {
                console.log(JSON.stringify({ type: 'complete', result: event.result }));
              } else {
                console.log(); // Newline after tokens
                console.log(chalk.gray('─'.repeat(50)));
                if (event.result) {
                  console.log(chalk.dim(`Model: ${event.result.model}`));
                  console.log(chalk.dim(`Execution Time: ${String(event.result.executionTimeMs)}ms`));
                  if (event.result.tokens) {
                    console.log(chalk.dim(`Tokens: ${String(event.result.tokens.prompt)} prompt + ${String(event.result.tokens.completion)} completion = ${String(event.result.tokens.total)} total`));
                  }
                  if (event.result.cost !== undefined) {
                    console.log(chalk.dim(`Cost: $${event.result.cost.toFixed(6)}`));
                  }
                }
                console.log();
              }
              break;

            case 'prompt.error':
              if (isJson) {
                console.log(JSON.stringify({ type: 'error', error: event.error }));
              } else {
                output.error(event.error ?? 'Unknown streaming error');
              }
              break;
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
